import type { EntityName, Project, SyncOperation, Task } from "./types";

const DB_NAME = "quiet-todos";
const DB_VERSION = 1;

interface StoreMap {
  tasks: Task;
  projects: Project;
  sync_queue: SyncOperation;
}

let connection: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (connection) return connection;
  connection = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("tasks")) {
        const store = db.createObjectStore("tasks", { keyPath: "id" });
        store.createIndex("user_id", "user_id");
        store.createIndex("updated_at", "updated_at");
      }
      if (!db.objectStoreNames.contains("projects")) {
        const store = db.createObjectStore("projects", { keyPath: "id" });
        store.createIndex("user_id", "user_id");
      }
      if (!db.objectStoreNames.contains("sync_queue")) {
        const store = db.createObjectStore("sync_queue", { keyPath: "id" });
        store.createIndex("created_at", "created_at");
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return connection;
}

function tx<T>(stores: Array<keyof StoreMap>, mode: IDBTransactionMode, run: (transaction: IDBTransaction) => Promise<T>): Promise<T> {
  return openDb().then((db) => {
    const transaction = db.transaction(stores, mode);
    return run(transaction);
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getAll<T extends keyof StoreMap>(storeName: T): Promise<StoreMap[T][]> {
  return tx([storeName], "readonly", async (transaction) => {
    return requestToPromise<StoreMap[T][]>(transaction.objectStore(storeName).getAll());
  });
}

export async function putLocal<T extends keyof StoreMap>(storeName: T, value: StoreMap[T]): Promise<void> {
  await tx([storeName], "readwrite", async (transaction) => {
    await requestToPromise(transaction.objectStore(storeName).put(value));
  });
}

export async function putMany<T extends keyof StoreMap>(storeName: T, values: StoreMap[T][]): Promise<void> {
  await tx([storeName], "readwrite", async (transaction) => {
    const store = transaction.objectStore(storeName);
    await Promise.all(values.map((value) => requestToPromise(store.put(value))));
  });
}

export async function removeLocal<T extends keyof StoreMap>(storeName: T, id: string): Promise<void> {
  await tx([storeName], "readwrite", async (transaction) => {
    await requestToPromise(transaction.objectStore(storeName).delete(id));
  });
}

export async function removeUserRows<T extends EntityName>(storeName: T, userId: string): Promise<void> {
  const rows = (await getAll(storeName)).filter((row) => row.user_id === userId);
  await tx([storeName], "readwrite", async (transaction) => {
    const store = transaction.objectStore(storeName);
    await Promise.all(rows.map((row) => requestToPromise(store.delete(row.id))));
  });
}

export async function queueOperation(operation: SyncOperation): Promise<void> {
  await putLocal("sync_queue", operation);
}

export async function readQueue(): Promise<SyncOperation[]> {
  const operations = await getAll("sync_queue");
  return operations.sort((a, b) => a.created_at.localeCompare(b.created_at));
}

export async function removeQueueItem(id: string): Promise<void> {
  await removeLocal("sync_queue", id);
}

export async function replaceRemoteRows(entity: EntityName, userId: string, rows: Array<Task | Project>): Promise<void> {
  const localRows = (await getAll(entity)).filter((row) => row.user_id === userId);
  const localById = new Map(localRows.map((row) => [row.id, row]));
  const merged = rows.filter((row) => {
    const local = localById.get(row.id);
    return !local || row.updated_at >= local.updated_at;
  });
  await tx([entity], "readwrite", async (transaction) => {
    const store = transaction.objectStore(entity);
    await Promise.all(merged.map((row) => requestToPromise(store.put(row as never))));
  });
}

export async function reconcileRemoteProjects(userId: string, remoteProjects: Project[]): Promise<string[]> {
  const [localProjects, operations] = await Promise.all([
    getAll("projects"),
    readQueue(),
  ]);
  const remoteIds = new Set(remoteProjects.map((project) => project.id));
  const protectedIds = new Set(
    operations
      .filter((operation) => operation.entity === "projects")
      .map((operation) => operation.record_id),
  );
  const removedIds = localProjects
    .filter((project) => project.user_id === userId)
    .filter((project) => !remoteIds.has(project.id) && !protectedIds.has(project.id))
    .map((project) => project.id);

  if (!removedIds.length) return [];

  const removedSet = new Set(removedIds);
  const tasksToClear = (await getAll("tasks"))
    .filter((task) => task.user_id === userId && task.project_id && removedSet.has(task.project_id))
    .map((task) => ({ ...task, project_id: null, updated_at: new Date().toISOString() }));

  await tx(["projects", "tasks"], "readwrite", async (transaction) => {
    const projectStore = transaction.objectStore("projects");
    const taskStore = transaction.objectStore("tasks");
    await Promise.all([
      ...removedIds.map((id) => requestToPromise(projectStore.delete(id))),
      ...tasksToClear.map((task) => requestToPromise(taskStore.put(task))),
    ]);
  });

  return removedIds;
}
